import { useCallback, useMemo } from 'react'

import { useLocation, useNavigate } from 'react-router-dom'

export const useSearchParams = <T = Record<string, string | undefined>>() => {
  const navigate = useNavigate()
  const { search, pathname } = useLocation()

  const params = useMemo(() => {
    const sp = new URLSearchParams(search)
    const p = Object.fromEntries(sp.entries()) as T
    Object.defineProperties(p, {
      get: {
        value: (key: string) => sp.get(key),
        enumerable: false
      },
      toString: {
        value: () => sp.toString(),
        enumerable: false
      }
    })
    return p as T & {
      get: (key: string) => string | null
      toString: () => string
    }
  }, [search])

  const setSearchParams = useCallback(
    (
      newParams:
        | Partial<T>
        | ((prev: T) => Partial<T>)
        | URLSearchParams
        | Record<string, string | number | undefined | null>,
      options?: { replace?: boolean }
    ) => {
      let searchString = ''

      if (newParams instanceof URLSearchParams) {
        searchString = newParams.toString()
      } else {
        const currentParams = new URLSearchParams(search)
        const nextParams =
          typeof newParams === 'function'
            ? (newParams as (prev: T) => Partial<T>)(params)
            : newParams

        Object.entries(nextParams).forEach(([key, value]) => {
          if (value === undefined || value === null) {
            currentParams.delete(key)
          } else {
            currentParams.set(key, String(value))
          }
        })
        searchString = currentParams.toString()
      }

      navigate(
        { pathname, search: searchString ? `?${searchString}` : '' },
        { replace: options?.replace }
      )
    },
    [navigate, search, pathname, params]
  )

  return [params, setSearchParams] as const
}
